import { Box, Button, Chip, CircularProgress, Grid, Paper, Table, TableBody, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import PieChartWithCenterLabel from "../../components/PieCenterLabel";
import SimpleAreaChart from "../../components/SimpleAreaChart";
import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/AuthContext";
import { useWaits } from "../../hooks/UseWait";
import Fetch from "../../services/Fetch";
import useSnackBar from "../../hooks/UseSnackBar";
import SnackbarAlert from "../../components/SnackBar";
import { useTheme } from '@mui/material/styles';
import RequestDetails from "../../popup/RequestDetails";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate } from "react-router-dom";
import { useTableStyles } from "../../hooks/UseTableStyles";
import { usePopups } from "../../hooks/UsePopups";
import { useConstants } from "../../hooks/UseConstants";
import { FormattedMessage } from "react-intl";
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import CastForEducationOutlinedIcon from '@mui/icons-material/CastForEducationOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import PageContent from "../../components/PageContent";
import DashboardStatCard from "../../components/cards/DashboardStatCard";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import PercentChangeFooter from "../../helper/PercentChangeFooter";
import LogoImage from "../../images/logo/logo.png";
import { APP_NAME } from "../../constants/brand";
import ModalOverlay from "../../components/ModalOverlay";

const emptyDashboard = {
    cards: {
        students: { total: 0, percent: 0 },
        teachers: { total: 0, percent: 0 },
        courses: { total: 0, percent: 0 },
        revenue: { total: 0, percent: 0 },
    },
    categories: [],
    monthly_chart: [],
};

function Dashboard() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { wait, profile } = useContext(AuthContext);
    const { setPopup } = usePopups();
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const [coursesRequests, setCoursesRequests] = useState([]);
    const [dashboard, setDashboard] = useState(emptyDashboard);
    const visibleRequests = coursesRequests.slice(0, 4);
    const [courseId, setCourseId] = useState(null);
    const [operation, setOperation] = useState(null);
    const [fromDate, setFromDate] = useState('2026-01-01');
    const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
    const [request, setRequest] = useState('');

    const getCoursesRequests = async () => {
        const result = await Fetch(host + '/courses?not_active=1', 'GET', null);
        if (result.status === 200) {
            const sorted = result.data.data.sort((a, b) =>
                a.status === 'pending' ? -1 : b.status === 'pending' ? 1 : 0
            );
            setCoursesRequests(sorted);
        }
    };

    const changeCourseStatus = async (id, status) => {
        setCourseId(id);
        setOperation(status);
        setSendWait(true);
        const formData = new FormData();
        formData.append('new_status', status);
        const result = await Fetch(host + `/admin/courses/${id}/change-status`, 'POST', formData);
        if (result.status === 200) {
            getCoursesRequests();
            setSnackBar('success', status === 'accepted' ? <FormattedMessage id='approved_msg' /> : <FormattedMessage id='rejected_msg' />);
            setCourseId(null);
        }
        setSendWait(false);
    };

    const getDashboard = async () => {
        const result = await Fetch(host + `/admin/reports/dashboard?from=${fromDate}&to=${toDate}`, 'GET', null);
        if (result.status === 200 && result.data?.data?.cards) {
            setDashboard({
                cards: result.data.data.cards,
                categories: result.data.data.categories || [],
                monthly_chart: result.data.data.monthly_chart || [],
            });
        } else {
            setDashboard(emptyDashboard);
            if (result.status !== 200) {
                setSnackBar('error', result.data?.errors?.[0] || result.data?.message || 'Failed to load dashboard');
            }
        }
    };

    const getCourseDetails = async (id) => {
        const result = await Fetch(`${host}/courses/${id}/show`, 'GET', null);
        if (result.status === 200) {
            setRequest(result.data.data);
        } else {
            setRequest(coursesRequests.find((item) => item.id === id));
        }
        setPopup('details', 'flex');
    };

    useEffect(() => {
        if (!wait) {
            const load = async () => {
                setGetWait(true);
                await getDashboard();
                await getCoursesRequests();
                setGetWait(false);
            };
            load();
        }
    }, [wait]);

    useEffect(() => {
        if (!wait) getDashboard();
    }, [fromDate, toDate, wait]);

    const displayName = profile?.first_name || (isAr ? 'مدير' : 'Admin');
    const { cards } = dashboard;

    const statCards = [
        {
            label: <FormattedMessage id="total_students" />,
            value: cards.students.total,
            icon: <SchoolOutlinedIcon />,
            accent: 'blue',
            footer: <PercentChangeFooter percent={cards.students.percent} />,
            onClick: () => navigate('/students'),
        },
        {
            label: <FormattedMessage id="total_teachers" />,
            value: cards.teachers.total,
            icon: <PeopleOutlineIcon />,
            accent: 'sky',
            footer: <PercentChangeFooter percent={cards.teachers.percent} />,
            onClick: () => navigate('/teachers'),
        },
        {
            label: <FormattedMessage id="total_courses" />,
            value: cards.courses.total,
            icon: <CastForEducationOutlinedIcon />,
            accent: 'teal',
            footer: <PercentChangeFooter percent={cards.courses.percent} />,
            onClick: () => navigate('/courses'),
        },
        {
            label: <FormattedMessage id="total_revenue" />,
            value: cards.revenue.total,
            icon: <AttachMoneyOutlinedIcon />,
            accent: 'green',
            footer: <PercentChangeFooter percent={cards.revenue.percent} />,
            onClick: () => navigate('/financial'),
        },
    ];

    if (getWait && !wait) {
        return (
            <Box className="w-4/5 h-screen relative flex justify-center items-center main-layout-content" sx={{ float: language === 'en' && 'right' }}>
                <CircularProgress size={70} />
            </Box>
        );
    }

    return (
        <>
            <PageContent wait={wait} loading={false}>
                <Box
                    className="home-hero-banner"
                    sx={{
                        position: 'relative',
                        borderRadius: 4,
                        overflow: 'hidden',
                        mb: 3,
                        minHeight: { xs: 220, md: 200 },
                        color: '#fff',
                        boxShadow: '0 20px 50px rgba(13, 53, 140, 0.18)',
                    }}
                >
                    <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #0D358C 0%, #1a5bb8 42%, #457b9d 78%, #040E26 100%)' }} />
                    <Box sx={{
                        position: 'absolute', width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.06)',
                        top: -70, insetInlineEnd: -50, animation: 'homeFloat 7s ease-in-out infinite',
                    }} />
                    <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, md: 4 }, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                        <Box className="hero-logo-card">
                            <Box component="img" src={LogoImage} alt={APP_NAME} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: '1.5rem', md: '1.85rem' }, lineHeight: 1.25 }}>
                                {APP_NAME}
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.92, mt: 1, lineHeight: 1.7, maxWidth: 520 }}>
                                {isAr
                                    ? 'لوحة الإدارة — تحكم بالطلاب والمدرسين والدورات والمالية من مكان واحد.'
                                    : 'Admin hub — manage students, teachers, courses, and finances in one place.'}
                            </Typography>
                            <Chip
                                label={isAr ? `مرحباً، ${displayName}` : `Welcome, ${displayName}`}
                                sx={{ mt: 1.5, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700 }}
                            />
                        </Box>
                    </Box>
                </Box>

                <ScrollReveal>
                    <Box className="flex justify-between flex-wrap gap-2 mb-3">
                        {statCards.map((card, i) => (
                            <DashboardStatCard
                                key={i}
                                {...card}
                                className="flex-[1_1_22%] min-w-[150px] mx-0 max-sm:flex-[1_1_45%]"
                            />
                        ))}
                    </Box>
                </ScrollReveal>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <ScrollReveal delay={80}>
                            <DashboardPanelCard
                                title={<FormattedMessage id="distribution_courses" />}
                                subtitle={<FormattedMessage id="main_categories" />}
                                className="mx-0 h-full"
                            >
                                <PieChartWithCenterLabel
                                    values={(dashboard.categories || []).map((c) => c.percent)}
                                    labels={(dashboard.categories || []).map((c) => (language === 'en' ? c.name_en : c.name_ar))}
                                    counts={(dashboard.categories || []).map((c) => c.count)}
                                />
                            </DashboardPanelCard>
                        </ScrollReveal>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <ScrollReveal delay={120}>
                            <DashboardPanelCard
                                title={<FormattedMessage id="platform_style" />}
                                subtitle={<FormattedMessage id="students_registrations" />}
                                className="mx-0 h-full"
                            >
                                <SimpleAreaChart
                                    monthlyChart={dashboard.monthly_chart || []}
                                    from={fromDate}
                                    to={toDate}
                                    values={(dashboard.monthly_chart || []).map((c) => c.total)}
                                    months={(dashboard.monthly_chart || []).map((c) => c.month)}
                                />
                                <Box className="mt-4 flex flex-wrap gap-2 justify-end" sx={{ flexDirection: language === 'en' ? 'row-reverse' : 'row' }}>
                                    <TextField value={toDate} onChange={(e) => setToDate(e.target.value)} type="date" label={<FormattedMessage id="to" />} size="small" />
                                    <TextField value={fromDate} onChange={(e) => setFromDate(e.target.value)} type="date" label={<FormattedMessage id="from" />} size="small" />
                                </Box>
                            </DashboardPanelCard>
                        </ScrollReveal>
                    </Grid>
                </Grid>

                <ScrollReveal delay={160}>
                    <DashboardPanelCard
                        title={<FormattedMessage id="approval_requests" />}
                        className="mx-0"
                        headerAction={
                            <Typography
                                onClick={() => navigate('/accept-requests')}
                                variant="body2"
                                className="cursor-pointer"
                                sx={{ color: '#2563EB', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                                <FormattedMessage id="view_all_requests" />
                                {language === 'en' ? <ArrowForwardIosOutlinedIcon fontSize="small" /> : <ArrowBackIosNewIcon fontSize="small" />}
                            </Typography>
                        }
                    >
                        <TableContainer component={Paper} dir={language === 'en' ? 'ltr' : 'rtl'} sx={{ boxShadow: 'none' }}>
                            <Table sx={{ minWidth: 700 }} aria-label="approval requests">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id="request_id" /></StyledTableCell>
                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id="course_name" /></StyledTableCell>
                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id="teacher_name" /></StyledTableCell>
                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id="request_date" /></StyledTableCell>
                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id="status" /></StyledTableCell>
                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id="procedures" /></StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleRequests.length ? visibleRequests.map((req) => (
                                        <StyledTableRow key={req.id}>
                                            <StyledTableCell
                                                align={language === 'en' ? 'left' : 'right'}
                                                className="cursor-pointer"
                                                onClick={() => req.status !== 'rejected' && req.status !== 'accepted' && getCourseDetails(req.id)}
                                            >
                                                {req.id}
                                            </StyledTableCell>
                                            <StyledTableCell align={language === 'en' ? 'left' : 'right'}>
                                                {language === 'en' ? req.name_en : req.name_ar}
                                            </StyledTableCell>
                                            <StyledTableCell align={language === 'en' ? 'left' : 'right'}>
                                                <Box className="flex items-center gap-2" sx={{ flexDirection: language === 'en' ? 'row' : 'row-reverse', justifyContent: language === 'en' ? 'flex-start' : 'flex-end' }}>
                                                    <Box className="w-7 h-7 rounded-full bg-gray-300 flex justify-center items-center font-bold text-sm">
                                                        {req.teacher?.first_name?.charAt(0) || '?'}
                                                    </Box>
                                                    {req.teacher?.first_name} {req.teacher?.last_name}
                                                </Box>
                                            </StyledTableCell>
                                            <StyledTableCell align={language === 'en' ? 'left' : 'right'}>{req.created_at.split(' ')[0]}</StyledTableCell>
                                            <StyledTableCell align={language === 'en' ? 'left' : 'right'}>
                                                <Box
                                                    className="text-center py-1 rounded-lg font-bold text-sm"
                                                    sx={{
                                                        backgroundColor: req.status === 'accepted' ? '#CCFFCC' : req.status === 'rejected' ? '#FF9999' : '#FCF0CF',
                                                        color: req.status === 'accepted' ? 'green' : req.status === 'rejected' ? 'red' : 'orange',
                                                    }}
                                                >
                                                    {req.status === 'accepted' ? <FormattedMessage id="approved" /> : req.status === 'rejected' ? <FormattedMessage id="rejected" /> : <FormattedMessage id="wait_approval" />}
                                                </Box>
                                            </StyledTableCell>
                                            <StyledTableCell align={language === 'en' ? 'left' : 'right'}>
                                                <Box sx={{ display: 'flex', gap: 1, flexDirection: language === 'en' ? 'row-reverse' : 'row' }}>
                                                    <Button
                                                        disabled={req.status === 'rejected' || req.status === 'accepted'}
                                                        onClick={() => changeCourseStatus(req.id, 'rejected')}
                                                        size="small"
                                                        variant="contained"
                                                        sx={{ bgcolor: '#fca5a5', color: '#b91c1c', '&:hover': { bgcolor: '#ef4444', color: '#fff' } }}
                                                    >
                                                        {sendWait && courseId === req.id && operation === 'rejected' ? <CircularProgress size={18} color="inherit" /> : <FormattedMessage id="reject" />}
                                                    </Button>
                                                    <Button
                                                        disabled={req.status === 'rejected' || req.status === 'accepted'}
                                                        onClick={() => changeCourseStatus(req.id, 'accepted')}
                                                        size="small"
                                                        variant="contained"
                                                        sx={{ bgcolor: '#86efac', color: '#15803d', '&:hover': { bgcolor: '#22c55e', color: '#fff' } }}
                                                    >
                                                        {sendWait && courseId === req.id && operation === 'accepted' ? <CircularProgress size={18} color="inherit" /> : <FormattedMessage id="approve" />}
                                                    </Button>
                                                </Box>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )) : (
                                        <StyledTableRow>
                                            <StyledTableCell colSpan={6} align="center">
                                                {isAr ? 'لا توجد طلبات معلّقة' : 'No pending requests'}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </DashboardPanelCard>
                </ScrollReveal>
            </PageContent>

            <ModalOverlay id="details" onClose={() => setPopup('details', 'none')}>
                <RequestDetails onClickClose={() => setPopup('details', 'none')} onClickAccept={changeCourseStatus} onClickReject={changeCourseStatus} request={request} />
            </ModalOverlay>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default Dashboard;
